// validador-nif-es.js - Validador completo de documentos españoles

const ValidadorNIF = {
  // Tablas de validación
  letrasNIF: 'TRWAGMYFPDXBNJZSQVHLCKE',
  letrasCIF: 'JABCDEFGHI',
  
  // Expresiones regulares para cada tipo
  patrones: {
    DNI: /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i,
    NIE: /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i,
    CIF: /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/i
  },

  // Validar DNI
  validarDNI(dni) {
    dni = dni.toUpperCase().trim();
    
    if (!this.patrones.DNI.test(dni)) {
      return { valid: false, tipo: 'DNI', error: 'Formato inválido' };
    }
    
    const numero = parseInt(dni.substr(0, 8));
    const letra = dni.substr(8, 1);
    const letraCalculada = this.letrasNIF[numero % 23];
    
    return {
      valid: letra === letraCalculada,
      tipo: 'DNI',
      numero: dni,
      error: letra !== letraCalculada ? 'Letra de control incorrecta' : null
    };
  },

  // Validar NIE
  validarNIE(nie) {
    nie = nie.toUpperCase().trim();
    
    if (!this.patrones.NIE.test(nie)) {
      return { valid: false, tipo: 'NIE', error: 'Formato inválido' };
    }
    
    // Convertir letra inicial a número
    let numero = nie;
    numero = numero.replace('X', '0').replace('Y', '1').replace('Z', '2');
    numero = parseInt(numero.substr(0, 8));
    
    const letra = nie.substr(8, 1);
    const letraCalculada = this.letrasNIF[numero % 23];
    
    return {
      valid: letra === letraCalculada,
      tipo: 'NIE',
      numero: nie,
      error: letra !== letraCalculada ? 'Letra de control incorrecta' : null
    };
  },

  // Validar CIF
  validarCIF(cif) {
    cif = cif.toUpperCase().trim();
    
    if (!this.patrones.CIF.test(cif)) {
      return { valid: false, tipo: 'CIF', error: 'Formato inválido' };
    }
    
    const letra = cif.charAt(0);
    const numero = cif.substr(1, 7);
    const control = cif.charAt(8);
    
    // Suma A (posiciones impares)
    let sumaA = 0;
    for (let i = 1; i < 8; i += 2) {
      let t = parseInt(numero.charAt(i - 1)) * 2;
      sumaA += t > 9 ? t - 9 : t;
    }
    
    // Suma B (posiciones pares)
    let sumaB = 0;
    for (let i = 2; i < 8; i += 2) {
      sumaB += parseInt(numero.charAt(i - 1));
    }
    
    // Cálculo del dígito de control
    const sumaTotal = sumaA + sumaB;
    const unidad = sumaTotal % 10;
    let digitoControl = (10 - unidad) % 10;
    
    // Verificar el tipo de control esperado
    const tiposConLetra = ['K', 'P', 'Q', 'S', 'N', 'W'];
    const debeSerLetra = tiposConLetra.includes(letra);
    
    let controlCalculado;
    if (debeSerLetra || isNaN(control)) {
      // Debe ser letra
      controlCalculado = this.letrasCIF[digitoControl];
    } else {
      // Debe ser número
      controlCalculado = digitoControl.toString();
    }
    
    return {
      valid: control === controlCalculado,
      tipo: 'CIF',
      numero: cif,
      tipoEmpresa: this.getTipoEmpresa(letra),
      error: control !== controlCalculado ? 'Dígito/letra de control incorrecto' : null
    };
  },

  // Obtener tipo de empresa por letra CIF
  getTipoEmpresa(letra) {
    const tipos = {
      'A': 'Sociedad Anónima',
      'B': 'Sociedad Limitada',
      'C': 'Sociedad Colectiva',
      'D': 'Sociedad Comanditaria',
      'E': 'Comunidad de Bienes',
      'F': 'Sociedad Cooperativa',
      'G': 'Asociación',
      'H': 'Comunidad de Propietarios',
      'J': 'Sociedad Civil',
      'N': 'Entidad No Residente',
      'P': 'Corporación Local',
      'Q': 'Organismo Autónomo',
      'R': 'Congregación Religiosa',
      'S': 'Órgano del Estado',
      'U': 'Unión Temporal',
      'V': 'Agrupación de Interés Económico',
      'W': 'Establecimiento Permanente'
    };
    return tipos[letra] || 'Tipo desconocido';
  },

  // Función principal de validación
  validar(documento) {
    if (!documento) {
      return { valid: false, error: 'Documento vacío' };
    }
    
    documento = documento.toUpperCase().trim();
    
    // Detectar tipo y validar
    if (this.patrones.DNI.test(documento)) {
      return this.validarDNI(documento);
    } else if (this.patrones.NIE.test(documento)) {
      return this.validarNIE(documento);
    } else if (this.patrones.CIF.test(documento)) {
      return this.validarCIF(documento);
    } else {
      return {
        valid: false,
        tipo: 'DESCONOCIDO',
        error: 'Formato no reconocido'
      };
    }
  }
};

// Integración con Vue.js
export default {
  methods: {
    validarDocumento() {
      const resultado = ValidadorNIF.validar(this.client.PartyIdentification);
      
      if (resultado.valid) {
        this.$store.commit("setSnackbar", {
          text: `✓ ${resultado.tipo} válido` + 
                (resultado.tipoEmpresa ? ` - ${resultado.tipoEmpresa}` : ''),
          color: "success"
        });
        
        // Si es empresa, podrías intentar VIES para doble verificación
        if (resultado.tipo === 'CIF') {
          this.checkVIES_Optional();
        }
      } else {
        this.$store.commit("setSnackbar", {
          text: `✗ ${resultado.error}`,
          color: "error"
        });
      }
      
      return resultado;
    }
  }
};

// Ejemplos de uso:
/*
console.log(ValidadorNIF.validar('12345678Z'));  // DNI válido
console.log(ValidadorNIF.validar('X1234567L'));  // NIE válido
console.log(ValidadorNIF.validar('A58265811'));  // CIF válido (La Caixa)
console.log(ValidadorNIF.validar('B65165458'));  // CIF válido
console.log(ValidadorNIF.validar('12345678A'));  // DNI inválido (letra incorrecta)
*/